import { useEffect, useState } from "react";
import {
  HiOutlineFolder,
  HiOutlineFolderOpen,
  HiChevronRight,
  HiChevronDown,
  HiOutlineFolderRemove,
} from "react-icons/hi";
import classes from "./TreeView.module.css";
import { Box, Group, Stack, Text } from "@mantine/core";
import { TreeConfigProps, TreeNodeProps, TreeViewProps } from "types/ui";

const CONFIG_DEFAULT = {
  id: "id",
  parent: "parent",
  children: "children",
  toReturn: [],
}

const highlightNodeText = (text: string, searchString: string): string | (string | JSX.Element)[] => {
  if (!text || !searchString) return text;

  const escapedRegexString = new RegExp(
    `(${searchString.replace(/([.*+?^${}()|[\]\\/\\])/g, "\\$1")})`,
    "gi"
  );
  const parts = text.split(escapedRegexString);

  return parts.map((part: string, index: number) => {
    if (part.toLowerCase() === searchString.toLowerCase()) {
      return (
        <Text
          component="span"
          key={index}
          c="blue"
          bg="white"
          style={{ borderRadius: "5px" }}
        >
          <b>{part}</b>
        </Text>
      );
    }
    return part;
  });
};

const filterNode = (
  data: object,
  searchString: string,
  config: TreeConfigProps
): object | null => {
  if (!data || typeof data !== "object") return null;

  const filteredChildren = (data[config.children] || [])
    .map((child: Record<string, any>) => filterNode(child, searchString, config))
    .filter(Boolean);

  if (data[config.parent]?.toLowerCase().includes(searchString.toLowerCase())) {
    const highlightedName = highlightNodeText(
      data[config.parent],
      searchString
    );

    return {
      ...data,
      [config.parent]: highlightedName,
      [config.children]: filteredChildren,
    };
  }

  if (filteredChildren.length > 0) {
    return {
      ...data,
      [config.children]: filteredChildren,
    };
  }

  return null;
};

const TreeNode: React.FC<TreeNodeProps> = ({
  config = CONFIG_DEFAULT,
  node = {},
  activeNode = null,
  setActiveNode = () => { },
  onSelectNode = () => { },
  expanded = false,
  expandAll = false,
}) => {
  const [isOpen, setIsOpen] = useState(expanded);

  useEffect(() => {
    setIsOpen(expanded || expandAll);
  }, [expandAll]);

  const handleToggle = (): void => {
    setIsOpen(!isOpen);
  };

  const handleNodeClick = (): void => {
    setActiveNode(node[config.id]);
    onSelectNode(config.toReturn.map((key: string) => node[key]));
  };

  return (
    <Stack gap={2}>
      <Group
        gap={8}
        px={8}
        py={6}
        wrap="nowrap"
        style={{
          cursor: "pointer",
          borderRadius: "6px",
          backgroundColor:
            activeNode === node[config.id] ? "#f1f3f5" : "transparent",
          transition: "background-color 0.15s ease",
        }}
        onClick={() => {
          handleNodeClick();
        }}
        onMouseEnter={(e) => {
          if (activeNode !== node[config.id]) {
            e.currentTarget.style.backgroundColor = "#f8f9fa";
          }
        }}
        onMouseLeave={(e) => {
          if (activeNode !== node[config.id]) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      >
        <Box
          style={{ width: '16px', height: '16px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { e.stopPropagation(); handleToggle(); }}
        >
          {node[config.children]?.length > 0 && (
            <Box style={{ color: '#495057', transition: 'transform 0.15s ease' }}>
              {isOpen ? <HiChevronDown size={16} /> : <HiChevronRight size={16} />}
            </Box>
          )}
        </Box>
        <Text component="span" size="sm" lh={1} style={{ color: '#868e96', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {isOpen && node[config.children]?.length > 0 ? (
            <HiOutlineFolderOpen size={15} />
          ) : (
            <HiOutlineFolder size={15} />
          )}
        </Text>
        <Text size="sm" style={{ flex: 1, lineHeight: 1.4, wordBreak: 'break-word' }}>{node[config.parent]}</Text>
      </Group>
      <Box
        className={`${classes.treeNode} ${isOpen ? classes.treeNodeExpanded : classes.treeNodeCollapsed
          }`}
      >
        {isOpen && node[config.children] && (
          <Box pl={24} style={{ borderLeft: "1.5px solid #e9ecef", marginLeft: '8px' }}>
            {node[config.children]?.map((childNode: Record<string, any>) => (
              <TreeNode
                key={childNode[config.id]}
                config={config}
                node={childNode}
                activeNode={activeNode}
                setActiveNode={setActiveNode}
                onSelectNode={onSelectNode}
                expandAll={expandAll}
              />
            ))}
          </Box>
        )}
      </Box>
    </Stack>
  );
};

const TreeView: React.FC<TreeViewProps> = ({
  data = {},
  dataFilter = "",
  config = CONFIG_DEFAULT,
  onSelectNode = () => { },
  expandFirst = false,
}) => {
  const [activeNode, setActiveNode] = useState<string | null>();
  const [items, setItems] = useState<object>(data);

  useEffect(() => {
    if (expandFirst) {
      setActiveNode(data[config.id]);
      onSelectNode(config.toReturn.map((key: string) => data[key]));
    }
  }, []);

  useEffect(() => {
    setItems(filterNode(data, dataFilter, config));
  }, [dataFilter]);

  return (
    <Box>
      {items &&
        [items]?.length > 0 &&
        [items].map((node: Record<string, any>) => (
          <TreeNode
            key={node[config.id]}
            config={config}
            node={node}
            activeNode={activeNode}
            setActiveNode={setActiveNode}
            onSelectNode={onSelectNode}
            expanded={expandFirst}
            expandAll={dataFilter !== ""}
          />
        ))}
      {!items && (
        <Group px="xs" gap="sm">
          <HiOutlineFolderRemove />
          No result found.
        </Group>
      )}
    </Box>
  );
};

export default TreeView;
